import type {
  ConferenceFilter,
  OrganizationRankingResponse,
  OrganizationRankingResponseRankingResponseRelatedPagedData,
  SportFilter,
} from '@on3/api';
import { On3IconSoftballLogoFull } from '@on3/icons';
import { useAuth } from '@on3/ui-lib/contexts/AuthProvider';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { externalApi } from '@on3/ui-lib/index';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import { MobileMPU } from '@public/components/Ads/MobileMPU';
import {
  Filter,
  makeConferenceList,
  makeList,
} from '@public/components/Db/Filters/Filter';
import { SortFilter } from '@public/components/Db/Filters/SortFilter';
import { ArrowRight } from '@public/components/Svg/ArrowRight';
import { PrimaryButton } from '@public/components/UI/Buttons/PrimaryButton';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { format } from 'date-fns';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import styles from './TeamRankings.module.scss';
import { TeamRow } from './TeamRow';

interface ITeamRankings
  extends OrganizationRankingResponseRankingResponseRelatedPagedData {
  filters: {
    years?: number[];
    sports?: SportFilter[];
    conferences?: ConferenceFilter[];
  };
}
interface ITeamRankingsSortByListProps {
  key: number;
  name: string;
  displayName: string;
}
const TeamRankingsSortByList: ITeamRankingsSortByListProps[] = [
  { key: 1, name: 'fivestar', displayName: '5-Star' },
  { key: 2, name: 'fourstar', displayName: '4-Star' },
  { key: 3, name: 'threestar', displayName: '3-Star' },
  { key: 4, name: 'averagerating', displayName: 'Avg. Rating' },
  { key: 5, name: 'totalcommits', displayName: 'Total Commits' },
  { key: 6, name: 'score', displayName: 'Score' },
];
const arr: any = [];
const adsPlacement = [10, 25, 50, 75, 100, 125, 150, 175];

export const TeamRankings = ({
  list = arr,
  relatedModel,
  pagination,
  filters,
}: ITeamRankings) => {
  const formatUpdate = useMemo(() => {
    if (relatedModel?.dateUpdated) {
      const updateDate = new Date(0).setUTCSeconds(relatedModel?.dateUpdated);
      const updateDay = format(updateDate, 'P');
      const updateTime = format(updateDate, 'pp');

      return `Last Updated on ${updateDay} at ${updateTime}`;
    }

    return null;
  }, [relatedModel?.dateUpdated]);

  const { sports, years, conferences } = filters;
  const router = useRouter();
  const { year, sport, rankingType, conference, sort } = router.query;
  const isSoftball = relatedModel?.sport?.key === 12;
  const [pageNumber, setPageNumber] = useState(2);
  const [disable, setDisable] = useState(false);
  const [teamList, setTeamList] = useState(list || []);
  const page = pageNumber;
  const isIndustry = useMemo(() => {
    return rankingType === 'industry-team';
  }, [rankingType]);
  const showLoadMoreButton =
    pagination?.count && teamList.length < pagination?.count;
  const { sportsData } = useSite();
  const { hasRdbLevel3Access } = useAuth();
  const handleChange = useCallback(() => {
    setDisable(true);
    const params = {
      page,
      conferenceAbbr: conference,
      sort,
    };

    externalApi
      .get(
        `/public/rdb/v1/team-ranking/${sport}-${year}/${
          isIndustry ? 'consensus-team-rankings' : 'team-rankings'
        }`,
        { params },
      )
      .then((r) => {
        setTeamList([...teamList, ...r.data.list]);
        setPageNumber(pageNumber + 1);
        setDisable(false);
      })
      .catch((e) => {
        alert(JSON.stringify(e?.response?.data?.message) || e?.message);
        setDisable(false);
      });
  }, [page, conference, sort, sport, year, isIndustry, teamList, pageNumber]);

  //**Removes Duplicates from the Conferences array This will be removed when BE removes duplicates*/
  const removeDuplicates = useCallback(
    (array: ConferenceFilter[]) =>
      array.reduce((acc, curr) => {
        const isNew = !acc.some(
          (newConference) => newConference.key === curr.key,
        );

        if (!isNew) {
          return [...acc];
        }

        return [...acc, { ...curr }];
      }, [] as ConferenceFilter[]),
    [],
  );

  const showIndustry = sportsData?.find(
    (sportData) => sportData.key === relatedModel?.sportKey,
  )?.isIndustryRankable;
  const canonicalURL = `https://www.on3.com/rivals/rankings/${
    showIndustry ? 'industry-team' : 'team'
  }/${sport}/${year}/`;

  const conferencesFilterList = useMemo(() => {
    const filteredConferences = conferences
      ? removeDuplicates(conferences)
      : [];

    filteredConferences.unshift({ key: -1, abbreviation: 'All' });

    return makeConferenceList(filteredConferences);
  }, [conferences, removeDuplicates]);

  useEffect(() => {
    if (list) {
      setTeamList([...list]);
    } else {
      setTeamList([]);
    }
  }, [list]);

  const setHeader = useMemo(() => {
    if (isIndustry) {
      return `${year} Industry Ranking ${sport} Team Recruiting Rankings`;
    }

    return `${year} ${sport} Team Recruiting Rankings`;
  }, [isIndustry, sport, year]);

  const getHref = useCallback(
    (sortType?: string) => {
      return {
        pathname: router.pathname,
        query: { ...router.query, sort: sortType },
      };
    },
    [router.pathname, router.query],
  );

  return (
    <section className={styles.teamListWrapper}>
      <Head>
        <link href={canonicalURL} rel="canonical" />
      </Head>
      <header>
        {hasRdbLevel3Access() && (
          <a
            href={`${databaseUrl}/rankings/${relatedModel?.key}/edit/`}
            rel="noopener noreferrer"
            target="_blank"
          >
            Edit
          </a>
        )}
        <Text className={styles.text} variant="h1">
          {setHeader}
        </Text>
      </header>

      <div className={styles.navContainer}>
        <span className={styles.filters}>
          <div className={styles.filterWrapper}>
            {sports && (
              <Filter list={sports} name="sport" selected={`${sport}`} />
            )}

            {years && (
              <Filter list={makeList(years)} name="year" selected={`${year}`} />
            )}
            <Filter
              buttonClassName={styles.btnConferenceFilter}
              list={conferencesFilterList}
              name="conference"
              selected={conference ? `${conference}` : 'all'}
            />
          </div>
        </span>
      </div>

      <div className={styles.metaBlock}>
        <div className={styles.blockOne}>
          {formatUpdate && (
            <Text className={styles.updateDate} variant="caption">
              {formatUpdate}
            </Text>
          )}
          {!!relatedModel?.averageCommits && (
            <Text className={styles.averageCommits} variant="caption">
              Avg Number of Commits used in Rankings Score:
              <span className={styles.averageCommitsNumber}>
                {relatedModel?.averageCommits}
              </span>
            </Text>
          )}
        </div>
        {showIndustry && (
          <div className={styles.blockTwo}>
            <TextLink
              className={styles.breakdownLink}
              href={`/rivals/rankings/team-breakdown/${sport}/${year}/`}
              underline="none"
              variant="subtitle1"
            >
              team rankings breakdown
            </TextLink>
          </div>
        )}
      </div>
      {isSoftball && (
        <div className={styles.softballBlock}>
          <span>Softball Rankings Provided by</span>
          <Link href="/softball/">
            <On3IconSoftballLogoFull height={36} width={80} />
          </Link>
        </div>
      )}
      <div className={styles.header}>
        <Text className={styles.rankHeader} variant="h6">
          Rank
        </Text>
        <div className={styles.starStyle}>
          <TextLink
            className={clsx(styles.btnSort, {
              [styles.active]: sort === 'fivestar',
            })}
            href={getHref('fivestar')}
            underline="none"
          >
            5-Stars
            {sort === 'fivestar' && <ArrowRight className={styles.arrow} />}
          </TextLink>
          <TextLink
            className={clsx(styles.btnSort, {
              [styles.active]: sort === 'fourstar',
            })}
            href={getHref('fourstar')}
            underline="none"
          >
            4-Stars
            {sort === 'fourstar' && <ArrowRight className={styles.arrow} />}
          </TextLink>
          <TextLink
            className={clsx(styles.btnSort, {
              [styles.active]: sort === 'threestar',
            })}
            href={getHref('threestar')}
            underline="none"
          >
            3-Stars
            {sort === 'threestar' && <ArrowRight className={styles.arrow} />}
          </TextLink>
          <TextLink
            className={clsx(styles.btnSort, {
              [styles.active]: sort === 'totalcommits',
            })}
            href={getHref('totalcommits')}
            underline="none"
          >
            Total Commits
            {sort === 'totalcommits' && <ArrowRight className={styles.arrow} />}
          </TextLink>
        </div>
        <TextLink
          aria-label="Average Rating"
          className={clsx(styles.btnSort, {
            [styles.active]: sort === 'averagerating',
          })}
          href={getHref('averagerating')}
          underline="none"
        >
          Avg. Rating
          {sort === 'averagerating' && <ArrowRight className={styles.arrow} />}
        </TextLink>
        <Text
          aria-label="Average NIL"
          className={styles.nilHeader}
          variant="subtitle2"
        >
          Avg. NIL
        </Text>
        <TextLink
          className={clsx(styles.btnSort, {
            [styles.active]: sort === 'score' || sort === undefined,
          })}
          href={getHref('score')}
          underline="none"
        >
          Score
          {(sort === 'score' || sort === undefined) && (
            <ArrowRight className={styles.arrow} />
          )}
        </TextLink>
        <SortFilter
          ascending={true}
          className={styles.mobileSort}
          paramName="sort"
          selected={`${sort ? sort : 'score'}`}
          sortList={TeamRankingsSortByList}
        />
      </div>
      <ol>
        {teamList?.map((team: OrganizationRankingResponse, index: number) => (
          <Fragment key={`${team.organization?.key}-${rankingType}-${sort}`}>
            {adsPlacement.includes(index) && (
              <li className={styles.ad}>
                <MobileMPU
                  id={`mobile_mpu_inc-${adsPlacement.indexOf(index) + 1}`}
                  incontent
                />
              </li>
            )}
            <li>
              <TeamRow {...team} conferenceRank={index + 1} />
            </li>
          </Fragment>
        ))}
      </ol>
      {showLoadMoreButton && (
        <PrimaryButton
          className={styles.btnLoadMore}
          disabled={disable}
          fullWidth={false}
          onClick={handleChange}
          variant="text"
        >
          <Text
            className={clsx(styles.txtLoadMore, {
              [styles.isDisabled]: disable,
            })}
            color="primary"
            variant="subtitle1"
          >
            Load More
          </Text>
        </PrimaryButton>
      )}
    </section>
  );
};
